// eslint-disable-next-line max-len
const COUNTRY_CODE_TOP_LEVEL_DOMAINS = 'acadaeafagaialamaoaqarasatauawaxazbabbbdbebfbgbhbibjbmbnbobrbsbtbwbybzcacccdcfcgchcickclcmcncocrcucvcwcxcyczdjdkdmdodzeceeegereseteufifjfkfmfofrgagdgegfggghgiglgmgngpgqgrgsgtgugwgyhkhmhnhrhthuidieiliminioiqirisitjejmjojpkekgkhkikmknkpkrkwkykzlalblclilklrlsltlulvlymamcmdmemgmhmkmlmmmnmompmqmrmsmtmumvmwmxmymznancnenfngninlnonpnrnunzompapepfpgphpkplpmpnprpsptpwpyqarerorsrurwsasbscsdsesgshsiskslsmsnsosrssstsvsxsysztctdtftgthtjtktltmtntotrtttvtwtzuaugukusuyuzvavcvevgvivnvuwfwsyeytzazmzw'.match(/.{1,2}(?=(.{2})+(?!.))|.{1,2}$/g);
const INFINITY_EXPIRE = new Date('Fri, 31 Dec 9999 23:59:59 UTC');
const REMOVAL_EXPIRE = new Date('Thu, 01 Jan 1970 00:00:00 UTC');
/**
 * Transform a given origin to a cookie domain by checking the provided domain against the country code top level domain and transform it to the
 * correct level.
 *
 * When setting a cookie we have to specify to which domain the cookie belongs. Unlike in local storage we can break legally the origin boundaries
 * by setting the cookie on the top level domain of current origin. For example setting the cookie of the origin www.sub1.example.com we will set the
 * cookie to .example.com. We recognize country code top level domains as well by setting a cookie from the origin www.sub1.example.co.uk to
 * .example.co.uk.
 *
 * @param {String} domain - The domain to transform
 * @return {String| undefined} The transformation result
 */
const transformOriginToCookieDomain = (domain) => {
  const domainParts = domain ? domain.split('.') : [];
  const lastDomainPart = domainParts[domainParts.length - 1];
  let cookieDomainParts = [];
  let domainPart = '';
  if (COUNTRY_CODE_TOP_LEVEL_DOMAINS.includes(lastDomainPart)) {
    // Process multi-dot top level domains (e.g. tx.to or co.uk)
    for (let i = domainParts.length - 1; i >= 0; i -= 1) {
      if (i >= domainParts.length - 3 && domainParts[i].length <= 4) {
        cookieDomainParts.push(domainParts[i]);
      } else {
        break;
      }
    }
    cookieDomainParts = cookieDomainParts.reverse();
    for (let i = 0, iLen = cookieDomainParts.length; i < iLen; i += 1) {
      // Remove common domain prefixes like www, m or mobile
      domainPart += `.${cookieDomainParts[i]}`;
    }
  } else {
    // Normal processing for none country code domains
    domainPart = domainParts
      .slice(domainParts.length - 2, domainParts.length)
      .join('.') || '';
  }
  if (domainPart.includes('.')) {
    domainPart = domainPart[0] === '.' ? domainPart : `.${domainPart}`;
  }
  return domainPart;
};
/**
 * Creates the default options for this instance
 *
 * @param {String} domain - Current domain extracted from window.location.hostname
 * @return {{path: string, expires: *, domain: *, sameSite: string, secure: boolean}}
 */
const createDefaultOptions = domain => ({
  path: '/',
  domain: transformOriginToCookieDomain(domain),
  expires: INFINITY_EXPIRE,
  secure: window.location.protocol.includes('https'),
  sameSite: 'strict',
});
/**
 * Transforms a cookie string to a object structure with cookie key and value
 *
 * @param {String} cookieString - The cookie string to transform
 * @return {{value: *, options: *}} - The transformed cookie string as object with cookie key and value
 */
const transformFromCookieString = (cookieString) => {
  const cookieParts = cookieString.split(';');
  const value = decodeURIComponent(cookieParts[0]);
  const options = {};
  const cookieOptions = cookieParts.slice(1, cookieParts.length);
  for (let i = 0, iLen = cookieOptions.length; i < iLen; i += 1) {
    const cookieOption = cookieOptions[i];
    if (cookieOption.includes('=')) {
      const cookieOptionParts = cookieOption.split('=');
      const [key, val] = cookieOptionParts;
      options[key] = val;
    } else {
      options[cookieOption] = true;
    }
  }
  if (options.expires) {
    options.expires = new Date(options.expires);
  }
  return {
    value,
    options,
  };
};
/**
 * Transform a key and a value to a cookie string with provided cookie options
 *
 * @param {String} key - The cookie key
 * @param {String} value - The cookie value
 * @param {Object} options - The cookie options
 * @param {String} [options.path] - The cookie path
 * @param {String} [options.domain] - The cookie domain
 * @param {Date} [options.expires] - The expires date of the cooke
 * @param {sameSite} [options.sameSite] - The same site cookie attribute
 * @return {String} Transformed cookie string
 */
const transformToCookieString = (key, value, options) => {
  // Make encoded key and value RFC6265 compliant
  const encodedKey = key
    .replace(/[^#$&+^`|]/g, encodeURIComponent)
    .replace(/\(/g, '%28')
    .replace(/\)/g, '%29');
  const encodedValue = encodeURIComponent(value);
  // Begin cookie string transform
  let cookieString = `${encodedKey}=${encodedValue}`;
  const optionKeys = Object.keys(options);
  for (let i = 0, iLen = optionKeys.length; i < iLen; i += 1) {
    const optionKey = optionKeys[i];
    if (options[optionKey]) {
      const optionValue = optionKey === 'expires' ? options[optionKey].toUTCString() : options[optionKey];
      cookieString += `;${optionKey}=${optionValue}`;
    } else {
      cookieString += '';
    }
  }
  return cookieString;
};
/**
 * Decodes a cookie an replace some RFC not-compliant chars with URI compliant chars
 *
 * @param {String} cookie - The cookie string to decode
 * @return {*} Decoded cookie string
 */
const decode = cookie => cookie.replace(/(%[\dA-F]{2})+/gi, decodeURIComponent);

export default class CookieStorage {
  /**
   * Getter for the enabled state of cookie storage
   *
   * @return {Boolean} Check result
   */
  get available() {
    try {
      const enabled = this.setItem(this.testKey, '1').getItem(this.testKey).value === '1';
      this.removeItem(this.testKey);
      return enabled;
    } catch (error) {
      return false;
    }
  }

  /**
   * Getter for all first party cookies
   *
   * @return {{value: *, key: *, options: *}[]}
   */
  // eslint-disable-next-line class-methods-use-this
  get items() {
    const cookieStrings = window.document.cookie ? window.document.cookie.split('; ') : [];
    return cookieStrings.map((cookieSting) => {
      const parts = cookieSting.split('=');
      let cookie = parts.slice(1).join('=');
      if (cookie.charAt(0) === '"') {
        cookie = cookie.slice(1, -1);
      }
      const key = decode(parts[0]);
      return { key, ...transformFromCookieString(cookie) };
    });
  }

  /**
   * Getter for the cookie storage stats
   *
   * @return {*}
   */
  get stats() {
    return {
      size: this.items.map(item => `${item.key};${item.value}`).join('; ').length,
      count: this.items.length,
    };
  }

  /**
   * Initiates a new instance of cookie storage
   *
   * @param {String} domain - The current domain of current origin (can be extracted from window.location.hostname)
   * @param {Object} [options] - Options for the cookie storage
   * @param {String} [options.path] - Cookie path
   * @param {String} [options.domain] - Cookie domain
   * @param {String} [options.expires] - Cookie expiration time
   * @param {String} [options.secure] - Secure cookie flag
   */
  constructor(domain, options) {
    this.name = 'CookieStorageClass';
    this.testKey = 'acsam_cookie_check';
    this.options = { ...options, ...createDefaultOptions(domain) };
  }

  /**
   * Sets the cookie by a given key with the provided value
   *
   * @param {String} key - The cookie key
   * @param {String} value - The cookie value
   * @param {Object} [options] - Optional options when defined it will overwrite the default options
   */
  setItem(key, value, options) {
    // Not implemented yet
    if (options && options.domain) {
      // Transform given domain (origin) to a compatible cross origin cookie domain
      // eslint-disable-next-line no-param-reassign
      options.domain = transformOriginToCookieDomain(options.domain);
    }
    window.document.cookie = transformToCookieString(key, value, options || this.options);
    return this;
  }

  /**
   * Gets a cookie by the provided key
   *
   * @param {String} key - The cookie key
   */
  // eslint-disable-next-line class-methods-use-this
  getItem(key) {
    return this.items.find(item => item.key === key) || { key, value: undefined, options: undefined };
  }

  /**
   * Removes a cookie by a given key
   *
   * @param {String} key - The cookie key
   */
  removeItem(key) {
    const options = { ...this.options, ...{ expires: REMOVAL_EXPIRE } };
    return this.setItem(key, undefined, options);
  }

  /**
   * Removes all keys which are identified by a provided regex
   *
   * @param {RegExp} regex - Regular expression for identifying
   * @return {*[]} Removed cookies
   */
  removeItems(regex) {
    const options = { ...this.options, ...{ expires: REMOVAL_EXPIRE } };
    const items = this.items.filter(item => regex.test(item.key));
    const results = [];
    for (let i = 0, iLen = items.length; i < iLen; i += 1) {
      const { key } = items[i];
      results.push(this.setItem(key, undefined, options));
    }
    return results;
  }
}
